import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { MatSnackBar } from '@angular/material/snack-bar';
import { DomainService } from 'src/app/services/domain/domain.service';
import { AuthService } from 'src/app/services/auth/auth.service';
import { StoragePermissionService } from 'src/app/services/storagePermission/storage-permission.service';
import { PickerService } from 'src/app/services/picker/picker.service';
import { DomainsModel } from 'src/app/models/domains.model';
import { LoginUserModel, LoginResponseModel } from 'src/app/models/loginuser.model';
import { RegisterUserModel, RegisterErrorModel } from 'src/app/models/registeruser.model';
import { ApplyPromoCode } from 'src/app/models/cart.model';
import { Lottery, Offer } from 'src/app/models/offer.model';
import { environment } from 'src/environments/environment';
import { Logtype } from 'src/app/enums/log/logtype.enum';
import { LogService } from 'src/app/services/log/log.service';
import { CartService } from 'src/app/services/cart/cart.service';
import { errorFraudMsg } from 'src/app/enums/errorMsg/fraud.enum';
import { SignInError } from 'src/app/enums/errorMsg/signIn.enum';
import { errorDefaultMsg } from 'src/app/enums/errorMsg/default.enum';
import { Language } from 'src/app/tools/language/language';
import { MarketingPixelsService } from 'src/app/services/marketingPixels/marketing-pixels.service';
import { RedirectToMainSiteService } from 'src/app/services/redirectToMainSite/redirect-to-main-site.service';
import { GlobalsService } from '../globals/globals.service';
import { MatDialog } from '@angular/material/dialog';
import { ModalConfirmationComponent } from 'src/app/components/modal-confirmation/modal-confirmation.component';
import { ModalInformationComponent } from 'src/app/components/modal-information/modal-information.component';

@Injectable({
  providedIn: 'root'
})
export class SignonService {
  domainInfo: DomainsModel;
  user: RegisterUserModel;
  registerError: RegisterErrorModel = null;
  lang: any = Language.currentLanguage.lenid;

  lottery: Lottery = environment.lottery;
  offer: Offer = this.lottery.hasOwnProperty('offer') ? this.lottery.offer : null;
  baseCount: number = this.offer && this.offer.hasOwnProperty('picks') ? this.offer.picks.count : null;
  ticketsByDraw: number = this.offer && this.offer.hasOwnProperty('multiplier') ? this.offer.multiplier : 1;
  basePick: any[] = [];
  extraPick: any[] = [];
  confirmRedirect = false;

  error$: Observable<any>;
  private errorSubject = new Subject<any>();

  token$: Observable<any>;
  private tokenSubject = new Subject<any>();

  constructor(
    private _domainService: DomainService,
    private _authService: AuthService,
    private _storageService: StoragePermissionService,
    private _logService: LogService,
    private _cartService: CartService,
    private _snackBar: MatSnackBar,
    private _marketingPixels: MarketingPixelsService,
    private _pickerService: PickerService,
    private _redirectToMainSite: RedirectToMainSiteService,
    private _global: GlobalsService,
    public dialog: MatDialog
  ) {
    this.domainInfo = this._domainService.getCurrentDomain();
    this.error$ = this.errorSubject.asObservable();
    this.token$ = this.tokenSubject.asObservable();
  }

  private getToken(username: string, password: string): void {
    const dataUser: LoginUserModel = { username, password };
    this._authService.loginUser(dataUser).subscribe(
      (response: LoginResponseModel) => {
        if (response.access_token) {
          this._authService.isUserLoggedBehavior.next(true);
          this._storageService.setToken(response);
          this.setTokenSubject('OK');
          this._storageService.setStorageProfile(JSON.stringify(dataUser.username));
        }
        if (response.error === 'invalid_credentials') {
          console.warn('invalid_credentials');
        }
      },
      (err) => {
        this._logService.createLog(Logtype.ERROR, err, this.constructor.toString().match(/\w+/g)[1], 'LoginComponent');
        console.warn('unexpected_error');
      }
    );
  }

  moveToParentSite(cart_id: any = false): any {
    const expireToken = String(this._storageService.getExpireToken());
    const accessToken = this._storageService.getToken();
    this._authService.singIn(this.user.email, accessToken, expireToken, cart_id).subscribe(
      (response) => {
        let { code, data } = response;
        if (code === 201) {
          if (data && data.hash) {
            this._storageService.setUserHash(data.hash);
            this._redirectToMainSite.siteRedirect(data.hash);
          }
        }
      },
      (error) => {
        this.errorLoginSnackbar(error);
      }
    );
  }

  moveToCasino(gameID: any = false): any {
    const expireToken = String(this._storageService.getExpireToken());
    const accessToken = this._storageService.getToken();
    this._authService.singInCasino(this.user.email, accessToken, expireToken, gameID).subscribe(
      (response) => {
        let { code, data } = response;
        if (code === 201) {
          if (data && data.hash) {
            this._storageService.setUserHash(data.hash);
            this._redirectToMainSite.siteRedirect(data.hash);
          }
        }
      },
      (error) => {
        this.errorLoginSnackbar(error);
      }
    );
  }

  registerWithTempCart(): void {
    this._authService.registerUser(this.user).subscribe(
      (response) => {
        let { code, data } = response;
        if (code === 201) {
          if (data && data.pixels && data.pixels.length) {
            this._marketingPixels.findPixel(data.pixels);
          }
          this.getToken(this.user.email, this.user.password);
        }
      },
      (err) => {
        if (err.error && (err.error.error && err.error.error.email) || (err.error.data && err.error.data.usr_email)) {
          this.setFormErrors(err);
        } else if (err.error && err.error.error && err.error.error.message) {
          const message = err.error.message;
          this.setFormErrorByCode(err.status, message);
        }
      },
      () => {
        this.createCartTemp();
      }
    );
  }

  registerHalfUserWithTempCart(): void {
    this._authService.registerUserHalfUser(this.user).subscribe(
      (response) => {
        let { code, data } = response;
        if (code === 200) {
          if (data && data.half_user) {
            this._storageService.setStorageProfile(JSON.stringify({email: data.half_user.email, identifier: data.half_user.identifier}));
          }
        }
      },
      (err) => {
        if (err.error && ((err.error.error && err.error.error.email) || (err.error.data && err.error.data.usr_email))) {
          this.setFormErrors(err);
        } else if (err.error && err.error.error && err.error.error.message) {
          const message = err.error.error.message[0];
          this.setFormErrorByCode(err.status, message);
        }
      },
      () => {
        this.createCartTempHalfUser();
      }
    );
  }

  registerFreeSpins(): void {
    let { type } = environment;
    this._authService.registerUser(this.user).subscribe(
      (response: any) => {
        let { code, data } = response;
        if (code === 201) {
          if (data && data.pixels && data.pixels.length) {
            this._marketingPixels.findPixel(data.pixels);
          }
          this.getToken(this.user.email, this.user.password);
        }
      },
      (err) => {
        if (err.error && (err.error.error && err.error.error.email) || (err.error.data && err.error.data.usr_email)) {
          this.setFormErrors(err);
        } else if (err.error && err.error.error && err.error.error.message) {
          const message = err.error.error.message[0];
          this.setFormErrorByCode(err.status, message);
        }
      },
      () => {
        if (type == 'casino-deposit') {
          this.token$.subscribe((tokenStatus) => tokenStatus == 'OK' && this.moveToParentSite());
        } else {
          this.moveToCasinoSection();
        }
      }
    );
  }

  createCartTemp(): void {
    this.token$.subscribe((token) => {
      if (token == 'OK') {
        this._cartService.createCartTemp().subscribe(
          (response) => {
            // TODO- Cambio para cuado se reactive el modal de redireccion
              // this.openDialog(response.data.cart_id, this.user.email);
            //END TODO
            this.redirectToParentSite(response.data.cart_id, this.user.email);
          },
          (error) => {
            this._logService.createLog(Logtype.ERROR, error, this.constructor.toString().match(/\w+/g)[1], 'CreateCart');
            this.setFormErrorByCode(error);
          }
        );
      }
    });
  }

  createCartTempHalfUser(): void {
    this._cartService.createCartTempHalfUser().subscribe(
      (response) => {
        const profile = this._storageService.getProfile()
        if(profile) this._redirectToMainSite.siteRedirectHalfUser(profile);

      },
      (error) => {
        this._logService.createLog(Logtype.ERROR, error, this.constructor.toString().match(/\w+/g)[1], 'CreateCart');
        this.setFormErrorByCode(error);
      }
    );
  }

  openDialog(cartId: any = false, usrEmail = null): void {
    this.dialog.open(ModalConfirmationComponent, {
      disableClose: true,
      width: '450px',
      data: { cartId, usrEmail }
    });
  }

  redirectToParentSite(cartId: any = false, usrEmail = null): any {
    const expireToken = String(this._storageService.getExpireToken());
    const accessToken = this._storageService.getToken();
    // TODO- Cambio para cuado se reactive el modal de redireccion
      // const { cartId, usrEmail } = this.data;
    //END TODO
    this._authService.singIn(usrEmail, accessToken, expireToken, cartId).subscribe(
      (response) => {
        let { code, data } = response;
        if (code === 201) {
          if (data && data.hash) {
            this._storageService.setUserHash(data.hash);
            this._redirectToMainSite.siteRedirect(data.hash);
          }
        }
      },
      (error) => {
        this.errorLoginSnackbar(error);
      }
    );
  }

  openModalInformation(): void {
    this.dialog.open(ModalInformationComponent, {
      disableClose: true,
      width: '450px',
      data: { id: 0 }
    });
  }

  moveToCasinoSection(): void {
    this.token$.subscribe((token) => {
      if (token == 'OK') {
        let gameID = this._storageService.getCasinoGame().game;
        this.moveToCasino(gameID);
      }
    });
  }

  applyCasinoDepositPcode(): void {
    let system = this._global.getSystem().site;
    let pcode: ApplyPromoCode = { promo_code: `${environment.pcode[system]}` };
    this.token$.subscribe((token) => {
      token === 'OK' &&
        this._cartService.applyOnlyPromoCode(pcode).subscribe(
          (result: any) => {
            result.code === 201 && this.moveToParentSite();
          },
          (err) => {
            this.setFormErrors(err);
          }
        );
    });
  }

  setFormErrorByCode(code, message = ''): void {
    let error: RegisterErrorModel = {
      errorMailExist: false,
      useSnackbar: false,
      loading: false,
      isStep: false,
      cpfInvalid: false,
      numberPhoneInvalid: false,
      invalidState: false
    };
    switch (Number(code)) {
      case 422: // User behaviour
        if (message.includes('Document Type')) error.cpfInvalid = true;
        if (message.includes('The email has already been taken')) error.errorMailExist = true;
        if (message.includes('Invalid state')) error.invalidState = true;
        if (message.includes('Enter a valid phone number'))
          error.numberPhoneInvalid = true;
          error.useSnackbar = false;
        break;
      case 451: // Fraud control
        error.useSnackbar = true;
        this.openModalInformation();
        break;
      default:
        error.useSnackbar = false;
        this.openModalInformation();
        break;
    }
    this.setErrorSubject(error);
  }

  setFormErrors(err): void {
    let errEmail = err?.error?.email || err?.error?.data?.usr_email;
    errEmail = typeof errEmail == 'undefined' ? err.error.message : errEmail;
    let error: RegisterErrorModel = {
      errorMailExist: false,
      useSnackbar: false,
      loading: false,
      isStep: false
    };
    switch (errEmail[0]) {
      case 'The usr email has already been taken.':
        error.errorMailExist = true;
        error.useSnackbar = false;
        break;
      case 'The email has already been taken.':
        error.errorMailExist = true;
        error.useSnackbar = false;
        break;
      case 'USER_ALREADY_REGISTERED':
        error.errorMailExist = true;
        error.useSnackbar = false;
        break;
      case 'possible_ip_fraud':
        error.useSnackbar = true;
        this.openModalInformation();
        break;
      case 'possible_fraud_duplicate_user':
        error.useSnackbar = true;
        this.openModalInformation();
        break;
      default:
        error.useSnackbar = false;
        break;
    }
    this.setErrorSubject(error);
  }

  errorLoginSnackbar(signIn: boolean = false) {
    let errorMsg = signIn ? SignInError[this.lang] : errorFraudMsg[this.lang];
    let snackError = this._snackBar.open(errorMsg, 'OK', {
      duration: 8000
    });
    snackError.onAction().subscribe(() => {
      this._redirectToMainSite.errorRedirect();
    });
    snackError.afterDismissed().subscribe(() => {
      this._redirectToMainSite.errorRedirect();
    });
  }

  private setErrorSubject(error: RegisterErrorModel): void {
    this.errorSubject.next(error);
  }

  private setTokenSubject(token: any): void {
    this.tokenSubject.next(token);
  }

}
